import React from "react";

/**
 * Tiny, safe Markdown-ish renderer for job descriptions.
 * Supports:
 *   ## Heading 2  /  ### Heading 3
 *   - bullet item  (or • bullet item)
 *   1. numbered item
 *   **bold**
 *   blank line = paragraph break
 *
 * Intentionally minimal — no raw HTML, no links, no images.
 * Output styled for the dark Careers theme.
 */
function renderInline(text, keyPrefix) {
  // Split by **bold** while preserving the markers
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return (
        <strong key={`${keyPrefix}-b-${i}`} className="text-white font-semibold">
          {p.slice(2, -2)}
        </strong>
      );
    }
    return <React.Fragment key={`${keyPrefix}-t-${i}`}>{p}</React.Fragment>;
  });
}

export default function JobDescription({ text }) {
  if (!text) return null;
  const lines = text.replace(/\r\n/g, "\n").split("\n");

  const blocks = [];
  let currentList = null; // { type: 'ul' | 'ol', items: [] }
  let currentPara = []; // array of lines

  const flushPara = () => {
    if (currentPara.length) {
      blocks.push({ kind: "p", text: currentPara.join(" ") });
      currentPara = [];
    }
  };
  const flushList = () => {
    if (currentList) {
      blocks.push(currentList);
      currentList = null;
    }
  };

  lines.forEach((raw) => {
    const line = raw.trim();

    if (!line) {
      flushPara();
      flushList();
      return;
    }

    const h3 = line.match(/^###\s+(.+)$/);
    const h2 = line.match(/^##\s+(.+)$/);
    const ul = line.match(/^[-•]\s+(.+)$/);
    const ol = line.match(/^\d+\.\s+(.+)$/);

    if (h2 || h3) {
      flushPara();
      flushList();
      blocks.push({ kind: h2 ? "h2" : "h3", text: (h2 || h3)[1] });
      return;
    }

    if (ul) {
      flushPara();
      if (!currentList || currentList.type !== "ul") {
        flushList();
        currentList = { kind: "list", type: "ul", items: [] };
      }
      currentList.items.push(ul[1]);
      return;
    }

    if (ol) {
      flushPara();
      if (!currentList || currentList.type !== "ol") {
        flushList();
        currentList = { kind: "list", type: "ol", items: [] };
      }
      currentList.items.push(ol[1]);
      return;
    }

    flushList();
    currentPara.push(line);
  });
  flushPara();
  flushList();

  return (
    <div className="space-y-5 text-white/85 text-sm md:text-base leading-relaxed">
      {blocks.map((b, i) => {
        if (b.kind === "h2") {
          return (
            <h4
              key={`h2-${i}`}
              className="heading text-lg md:text-xl text-white uppercase tracking-tight pt-2 flex items-center gap-3"
            >
              <span className="inline-block w-6 h-[2px] bg-signal" />
              {b.text}
            </h4>
          );
        }
        if (b.kind === "h3") {
          return (
            <h5
              key={`h3-${i}`}
              className="font-mono text-xs tracking-[0.2em] uppercase text-signal pt-1"
            >
              {b.text}
            </h5>
          );
        }
        if (b.kind === "list") {
          const ListTag = b.type === "ol" ? "ol" : "ul";
          return (
            <ListTag key={`list-${i}`} className="space-y-2 pl-0">
              {b.items.map((item, j) => (
                <li key={`list-${i}-item-${j}`} className="flex items-start gap-3">
                  <span className="text-signal mt-[8px] w-2 h-[2px] bg-signal inline-block flex-shrink-0" />
                  <span>{renderInline(item, `${i}-${j}`)}</span>
                </li>
              ))}
            </ListTag>
          );
        }
        return (
          <p key={`p-${i}`}>
            {renderInline(b.text, `p-${i}`)}
          </p>
        );
      })}
    </div>
  );
}
